import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useStorageUrl } from '@/hooks/useStorageUrl';
import { cn } from '@/lib/utils';

interface ExploreImageProps {
  path: string | undefined;
  alt: string;
  /** Drawn on the fallback — a place's pin, a tour's ticket. */
  icon: LucideIcon;
  className?: string;
}

/**
 * A destination photo with a fallback that is never a broken image.
 *
 * The gradient tile and its icon are always painted underneath; the photo
 * fades in over them once its URL resolves and the bytes load. So the same
 * frame covers every case without telling them apart — no photo uploaded,
 * the URL still resolving, the file deleted, the network dropped — and a card
 * never jumps in height when its photo arrives.
 */
export function ExploreImage({ path, alt, icon: Icon, className }: ExploreImageProps) {
  const url = useStorageUrl(path);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const visible = url !== null && loadedUrl === url && failedUrl !== url;

  return (
    <div
      className={cn(
        'relative overflow-hidden bg-[linear-gradient(135deg,oklch(0.55_0.12_215)_0%,oklch(0.42_0.14_250)_100%)]',
        className,
      )}
    >
      <div className="absolute inset-0 flex items-center justify-center text-white/45" aria-hidden="true">
        <Icon className="size-10" strokeWidth={1.5} />
      </div>
      {url && failedUrl !== url && (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          onLoad={() => setLoadedUrl(url)}
          onError={() => setFailedUrl(url)}
          className={cn(
            'absolute inset-0 size-full object-cover transition-opacity duration-300',
            visible ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
    </div>
  );
}
